import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState('all');
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchImages();
  }, [selectedAlbum, selectedSeason]);

  const fetchData = async () => {
    try {
      const [albumsRes, seasonsRes] = await Promise.all([
        axios.get(`${API}/gallery/albums`),
        axios.get(`${API}/gallery/seasons`),
      ]);
      setAlbums(albumsRes.data);
      setSeasons(seasonsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchImages = async () => {
    try {
      let url = `${API}/gallery`;
      const params = [];
      if (selectedAlbum !== 'all') params.push(`album=${selectedAlbum}`);
      if (selectedSeason !== 'all') params.push(`season=${selectedSeason}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1551958219-acbc608c6377?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Memórias</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">GALERIA</h1>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-[#0A0A0A] border-b border-white/10 sticky top-20 z-40">
        <div className="container-custom section-padding">
          <div className="flex flex-wrap items-center gap-6 py-4">
            {/* Albums */}
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Álbum:</span>
              <select
                value={selectedAlbum}
                onChange={(e) => setSelectedAlbum(e.target.value)}
                className="bg-transparent border border-white/20 text-white px-4 py-2 text-sm focus:outline-none focus:border-white/40"
                data-testid="album-filter"
              >
                <option value="all" className="bg-[#0A0A0A]">Todos</option>
                {albums.map((album) => (
                  <option key={album} value={album} className="bg-[#0A0A0A]">{album}</option>
                ))}
              </select>
            </div>

            {/* Seasons */}
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Época:</span>
              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                className="bg-transparent border border-white/20 text-white px-4 py-2 text-sm focus:outline-none focus:border-white/40"
                data-testid="season-filter"
              >
                <option value="all" className="bg-[#0A0A0A]">Todas</option>
                {seasons.map((season) => (
                  <option key={season} value={season} className="bg-[#0A0A0A]">{season}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 section-padding" data-testid="gallery-grid">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="aspect-square bg-[#0A0A0A] border border-white/10 skeleton" />
              ))}
            </div>
          ) : images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="gallery-item aspect-square cursor-pointer border border-white/10 hover:border-white/30 transition-colors"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.image_url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 border border-white/10">
              <ImageIcon size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-gray-500">Nenhuma imagem encontrada</p>
              <p className="text-gray-600 text-sm mt-2">As fotos serão adicionadas em breve</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="lightbox">
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-6 text-white hover:text-gray-300 transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={48} />
          </button>

          <div className="max-w-5xl max-h-[80vh] mx-auto px-4">
            <img
              src={images[currentImageIndex].image_url}
              alt={images[currentImageIndex].title}
              className="max-w-full max-h-[70vh] object-contain mx-auto"
            />
            <div className="text-center mt-6">
              <h3 className="font-heading font-bold text-xl">{images[currentImageIndex].title}</h3>
              {images[currentImageIndex].description && (
                <p className="text-gray-400 mt-2">{images[currentImageIndex].description}</p>
              )}
              <p className="text-gray-500 text-sm mt-2">
                {images[currentImageIndex].album} • {images[currentImageIndex].season}
              </p>
            </div>
          </div>

          <button
            onClick={nextImage}
            className="absolute right-6 text-white hover:text-gray-300 transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight size={48} />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
